import React from "react";
import {
	ChildrenProps,
	DefaultContextValue,
	evaluateTypeOrFunction,
	TypeOrFunction,
	useCount,
	useRequiredContext,
} from "@hex-insights/core";

export type WizardNavigationContextValue = {
	page: number;
	minPage: number;
	maxPage: number;
	incrementPage: () => void;
	decrementPage: () => void;
	setPage: (page: number) => void;
};

export const WizardNavigationContext = React.createContext<WizardNavigationContextValue>(
	new DefaultContextValue("WizardNavigationContext") as any,
);

export function useWizardNavigation<T extends number>() {
	const { page, minPage, maxPage, setPage, incrementPage, decrementPage } = useRequiredContext(WizardNavigationContext);

	const typedSetPage = React.useCallback((page: T) => setPage(page), [setPage]);

	return {
		page: page as T,
		minPage: minPage as T,
		maxPage: maxPage as T,
		setPage: typedSetPage,
		incrementPage,
		decrementPage,
	};
}

export type WizardNavigationRouterProps<T extends number> = {
	pages: T[];
	initialPage?: TypeOrFunction<T>;
} & Partial<ChildrenProps>;

export function WizardNavigationRouter<T extends number>({
	pages,
	initialPage,
	children,
}: WizardNavigationRouterProps<T>) {
	const {
		count: page,
		increment: incrementPage,
		decrement: decrementPage,
		setCount: setPage,
	} = useCount(evaluateTypeOrFunction(initialPage) ?? pages[0], { min: pages[0], max: pages[pages.length - 1] });
	const contextValue = React.useMemo(
		() => ({
			page,
			minPage: pages[0],
			maxPage: pages[pages.length - 1],
			incrementPage,
			decrementPage,
			setPage,
		}),
		[page, pages, incrementPage, decrementPage, setPage],
	);

	return <WizardNavigationContext.Provider value={contextValue}>{children}</WizardNavigationContext.Provider>;
}

export type WizardNavigationRouteProps<T extends number> = {
	page: T;
} & Partial<ChildrenProps>;

export function WizardNavigationRoute<T extends number>({ page, children }: WizardNavigationRouteProps<T>) {
	const { page: currentPage, minPage, maxPage } = useWizardNavigation();

	const shouldRender =
		page === currentPage ||
		(currentPage <= minPage && page === minPage) ||
		(currentPage >= maxPage && page === maxPage);

	if (!shouldRender) {
		return null;
	}

	return <React.Fragment>{children}</React.Fragment>;
}
